import { useMemo } from 'react';
import { breakpointSizes } from '@theme/foundations/breakpoints';
import { ROOT_ID } from '@view/constants/root';
import { useElementSize } from './useElementSize';
import { useViewportSize } from './useViewportSize';
export const useAppSize = () => {
    const element = document.querySelector(`#${ROOT_ID}`);
    const { width: elementWidth } = useElementSize(element);
    const { width: viewportWidth } = useViewportSize();
    // Fallback to viewport width in case the element's width is not defined or is zero, also needed for the integration tests
    // When zoom is applied, the element width can be float, so we need to convert it to an integer to prevent issues in the media queries and calculations
    const width = (elementWidth && Math.floor(elementWidth)) || viewportWidth;
    // We use less or equal for the upper breakpoint and strict greater for the lower breakpoint
    const { isGreaterThanLg, isGreaterThanMd, isGreaterThanSm, isLarge, isLessOrEqualThanLg, isLessOrEqualThanMd, isLessOrEqualThanSm, isMedium, isSmall, isXSmall, } = useMemo(() => ({
        isGreaterThanLg: width > breakpointSizes.lg,
        isGreaterThanMd: width > breakpointSizes.md,
        isGreaterThanSm: width > breakpointSizes.sm,
        isLarge: width > breakpointSizes.md && width <= breakpointSizes.lg,
        isLessOrEqualThanLg: width <= breakpointSizes.lg,
        isLessOrEqualThanMd: width <= breakpointSizes.md,
        isLessOrEqualThanSm: width <= breakpointSizes.sm,
        isMedium: width > breakpointSizes.sm && width <= breakpointSizes.md,
        isSmall: width <= breakpointSizes.sm,
        isXSmall: width <= breakpointSizes.xs,
    }), [width]);
    const isDomReady = useMemo(() => width !== null, [width]);
    const size = useMemo(() => {
        if (!isDomReady) {
            return 'lg';
        }
        if (width <= breakpointSizes.xs) {
            return 'xs';
        }
        if (width <= breakpointSizes.sm) {
            return 'sm';
        }
        if (width <= breakpointSizes.md) {
            return 'md';
        }
        return 'lg';
    }, [isDomReady, width]);
    const fontSize = useMemo(() => {
        if (width < 320) {
            return '12px';
        }
        if (width < 375) {
            return '14px';
        }
        return '16px';
    }, [width]);
    return {
        fontSize,
        isDomReady,
        isGreaterThanLg,
        isGreaterThanMd,
        isGreaterThanSm,
        isLarge,
        isLessOrEqualThanLg,
        isLessOrEqualThanMd,
        isLessOrEqualThanSm,
        isMedium,
        isSmall,
        isXSmall,
        size,
    };
};
